import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./SurveyCreator.module.css";

import SurveyCard from "../SurveyCard";
import { getSurveys, createEmptySurvey, fetchDatabase, getSurvey } from "../../utils/APIConnector";
import { clearSurveyResponses, deleteSurvey } from "../../utils/APIConnector/SurveyDatabase";
import { databaseToCSV, generateAnswersLabels, generateColumnsLabels, createExportZip, downloadZip } from "../../utils/exportCSV";
import ClearResponsesModal from "./ClearResponsesModal";
import DeleteSurveyModal from "./DeleteSurveyModal";

export default function SurveyCreator() {
    const { t, i18n } = useTranslation();
    const [surveys, setSurveys] = useState([]);
    const [exporting, setExporting] = useState(null);
    const [clearing, setClearing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [showClearModal, setShowClearModal] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        getSurveys()
            .then((data) => setSurveys(data))
            .catch((err) => console.error(err));
    }, []);

    function createNewSurvey() {
        createEmptySurvey()
            .then((data) => {
                // Navigate to the new survey's edit page
                navigate(`/create-survey/${data.surveyID}`);
            })
            .catch((err) => {
                console.error(err);
                alert(t("createSurvey.error.permission"));
            });
    }

    async function handleExportCSV(surveyID, e) {
        e.preventDefault();
        e.stopPropagation();

        setExporting(surveyID);
        try {
            // Fetch database and survey metadata
            const [database, metadata] = await Promise.all([
                fetchDatabase(surveyID),
                getSurvey(surveyID)
            ]);

            // Convert to CSV
            const csvContent = databaseToCSV(database, metadata, i18n.language);

            if (!csvContent) {
                alert(t("export.noData", { defaultValue: "Brak danych do eksportu" }));
                return;
            }

            // Generate labels JSON files
            const answersLabels = generateAnswersLabels(metadata, i18n.language);
            const columnsLabels = generateColumnsLabels(metadata, i18n.language, database);

            // Create ZIP with all files
            const zipBlob = await createExportZip(csvContent, answersLabels, columnsLabels, surveyID);

            // Download ZIP
            const filename = `survey-${surveyID}-export-${new Date().toISOString().split('T')[0]}.zip`;
            downloadZip(zipBlob, filename);
        } catch (err) {
            console.error('Export failed:', err);
            alert(t("export.error", { defaultValue: "Błąd podczas eksportu: " }) + (err.message || err));
        } finally {
            setExporting(null);
        }
    }

    async function handleClearResponses(surveyID, confirmedID) {
        setClearing(surveyID);
        try {
            const result = await clearSurveyResponses(surveyID, confirmedID);
            alert(t("clearResponses.success", { defaultValue: "Odpowiedzi zostały wyczyszczone. " }) + result.detail);
            setShowClearModal(null);
        } catch (err) {
            console.error('Clear responses failed:', err);
            alert(t("clearResponses.error", { defaultValue: "Błąd podczas czyszczenia odpowiedzi: " }) + (err.message || err));
        } finally {
            setClearing(null);
        }
    }

    async function handleDeleteSurvey(surveyID, confirmedID) {
        setDeleting(surveyID);
        try {
            const result = await deleteSurvey(surveyID, confirmedID);
            alert(t("deleteSurvey.success", { defaultValue: "Ankieta została usunięta. " }) + result.detail);
            setShowDeleteModal(null);
            // Remove deleted survey from list
            setSurveys(surveys.filter(s => s.surveyID !== surveyID));
        } catch (err) {
            console.error('Delete survey failed:', err);
            alert(t("deleteSurvey.error", { defaultValue: "Błąd podczas usuwania ankiety: " }) + (err.message || err));
        } finally {
            setDeleting(null);
        }
    }

    return (
        <div className={styles.wrapper}>
            <h1>{t("createSurvey.tableTitle")}</h1>

            <div
                role="list"
                aria-label={t("surveys.listAria")}
                className="survey-list"
            >
                {surveys.map((survey) => {
                    const id = survey.surveyID;
                    const lang = i18n.language;
                    return (
                        <div key={id} style={{ position: 'relative' }}>
                            <Link
                                to={`/create-survey/${id}`}
                                role="listitem"
                                aria-label={survey?.title?.[lang] ?? survey?.title?.en ?? "Survey"}
                                style={{ display: "block", textDecoration: "none", color: "inherit" }}
                            >
                                <SurveyCard survey={survey} lang={lang} />
                            </Link>
                            <div className={styles.buttonGroup}>
                                <button
                                    className={styles.exportButton}
                                    onClick={(e) => handleExportCSV(id, e)}
                                    disabled={exporting === id || clearing === id || deleting === id}
                                    title={t("export.zip", { defaultValue: "Eksportuj odpowiedzi" })}
                                >
                                    {exporting === id ? t("export.exporting", { defaultValue: "Eksportowanie..." }) : "⭳"}
                                </button>
                                <button
                                    className={styles.clearButton}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowClearModal(id);
                                    }}
                                    disabled={clearing === id || exporting === id || deleting === id}
                                    title={t("clearResponses.title", { defaultValue: "Wyczyść odpowiedzi" })}
                                >
                                    {clearing === id ? "⏳" : "🗑️"}
                                </button>
                                <button
                                    className={styles.deleteButton}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowDeleteModal(id);
                                    }}
                                    disabled={deleting === id || exporting === id || clearing === id}
                                    title={t("deleteSurvey.title", { defaultValue: "Usuń ankietę" })}
                                >
                                    {deleting === id ? "⏳" : "❌"}
                                </button>
                            </div>
                            {showClearModal === id && (
                                <ClearResponsesModal
                                    surveyID={id}
                                    onConfirm={(confirmedID) => handleClearResponses(id, confirmedID)}
                                    onCancel={() => setShowClearModal(null)}
                                    isLoading={clearing === id}
                                />
                            )}
                            {showDeleteModal === id && (
                                <DeleteSurveyModal
                                    surveyID={id}
                                    onConfirm={(confirmedID) => handleDeleteSurvey(id, confirmedID)}
                                    onCancel={() => setShowDeleteModal(null)}
                                    isLoading={deleting === id}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            <button className={styles.newSurveyButton} onClick={createNewSurvey}>
                {t("createSurvey.createNew")}
            </button>
        </div>
    );
}